// Drives the 404 page's own search field (layouts/404.html). A trimmed
// version of search-page.ts's bindInput()/syncQueryState() — this page has
// no filter pane, no recently-viewed list, no clear-filters button, so none
// of that page's other wiring applies here.
import { trackSearchInput } from "./analytics";

class NotFoundSearch {
    private _shell: HTMLElement | null = null;
    private _input: HTMLInputElement | null = null;

    init() {
        this._shell = document.querySelector<HTMLElement>("#notfound-search-panel");
        if (!this._shell) {
            return;
        }

        this.bindInput();
    }

    // <pagefind-input> renders its own native <input> into the light DOM
    // once pagefind-component-ui.js upgrades the element — see search.ts's
    // bindInput() for why the MutationObserver fallback exists.
    private bindInput() {
        if (!this._shell) {
            return;
        }

        const tryBind = (): boolean => {
            const input = this._shell?.querySelector<HTMLInputElement>("pagefind-input input");
            if (!input) {
                return false;
            }

            this._input = input;
            input.addEventListener("input", () => this.syncQueryState());
            trackSearchInput(input, "404");
            this.syncQueryState();
            // Whoever lands here is trying to find something right now —
            // focus immediately rather than making them tap the field
            // again. 16px input font (search.scss's .search-page-shell)
            // clears iOS Safari's auto-zoom-on-focus threshold.
            input.focus();
            return true;
        };

        if (tryBind()) {
            return;
        }

        const observer = new MutationObserver(() => {
            if (tryBind()) {
                observer.disconnect();
            }
        });
        observer.observe(this._shell, { childList: true, subtree: true });
    }

    // The shell must fully hide .search-body on an empty query rather than
    // show it with a stale/empty result list — data-query drives that in
    // search.scss.
    private syncQueryState() {
        if (!this._input || !this._shell) {
            return;
        }

        this._shell.dataset.query = this._input.value.trim() ? "active" : "empty";
    }
}

export default new NotFoundSearch();
